import { OpeningThoughtWithConversationDTO, ThoughtInDiscussionDTO } from "@/generated-client";
import type { ThoughtPinFields } from "@/main/common/ThoughtPinTypes";
import type { ThoughtAcceptFields } from "@/main/common/QuestionFacetTypes";
import type { ThoughtMindChangedFields } from "@/main/common/MindChangedTypes";

/** A top-level thought as the discussion list returns it, plus the pin, accepted-answer and
 *  changed-my-mind fields the generated client does not know about yet (AGENTS rule 6 — the DTO
 *  gained them without a client regeneration). All three ride on the same projection, so all three are
 *  read through the same narrow cast. */
export type OpeningThoughtWithPinDTO = ThoughtInDiscussionDTO
    & ThoughtPinFields
    & ThoughtAcceptFields
    & ThoughtMindChangedFields;

export type OpeningThoughtWithGroupMatchesDTO = Omit<OpeningThoughtWithConversationDTO, "openingArgument"> & {
    openingArgument?: OpeningThoughtWithPinDTO;
    matchedDiscussionGroupPublicIds?: string[];
};

/**
 * The pinned thought in a loaded page, if any.
 *
 * Reading it off the loaded list is reliable because the pinned thought is always returned FIRST on
 * page 1 and the island always holds page 1 — and if it were ever wrong, the failure mode is a missing
 * confirmation dialog, not a wrong write: the server replaces the pin either way.
 */
export function findPinnedOpeningThought(
    items?: OpeningThoughtWithGroupMatchesDTO[],
): OpeningThoughtWithPinDTO | undefined {
    return (items ?? [])
        .map((item) => item?.openingArgument)
        .find((thought) => Boolean(thought?.pinned));
}

/**
 * The ordering the SERVER renders top-level thoughts with, pinned in
 * `DiscussionConversationController.loadTopLevelRepliesForSsr`. It is deliberately NOT the client
 * default (`"best"`, below): the same query feeds the JSON-LD `comment[]` and the QAPage answers, so
 * changing it is an SEO change.
 *
 * <p>It is the FALLBACK, not the source of truth. The server declares the seed's ordering on the
 * payload element itself (`data-thoughts-ordering`, emitted since the SSR handoff shipped and read by
 * nothing until 2026-09-08 — register F20), and `DiscussionPage`'s `getProps` reads it. This constant
 * answers only for a page rendered by a template older than that read, which keeps the two deploy
 * halves order-independent. So "keep the two in sync" is no longer an obligation a reader has to
 * remember: a template that changes its SSR ordering says so on the element, and the client believes
 * the element.
 */
export const SSR_SEEDED_THOUGHTS_ORDERING: DiscussionOpeningThoughtOrderingStyle = "new";

/**
 * Strip the keyset cursor from server-inlined thoughts when the viewer asked for a different ordering.
 *
 * The rows themselves stay — showing them immediately is the entire point of the D1 seed handoff, and
 * rule 32 forbids rendering less than the HTML we shipped. Their `paginationParameter` cannot stay,
 * because the cursor's SHAPE is ordering-specific (`iso|id` for `new`/`old`, `score|iso|id` for
 * `best`/`controversial`, count-prefixed for the rest — see
 * `ThoughtRepositoryImpl.addOpeningArgumentThreadCursorParams`). SSR is pinned to `"new"` and the
 * client default is `"best"`, so a seeded cursor is the wrong shape on every default load, and "load
 * more" answers 400 `Invalid paginationParameter`.
 *
 * A cursor-less last row is already understood downstream: `PaginatedListWrapperV2`'s
 * `inferHasMoreFromItems` reads it as "no more pages" and logs why. So pagination simply stays off
 * until the background fetch lands rows whose cursors match the requested ordering — including in the
 * terminal-failure branch that RETAINS the seed, where the wrong-shaped cursor would otherwise make
 * every subsequent load-more fail rather than just the ones during the mount race.
 *
 * Found in prod 2026-07-27, the day the seed handoff shipped:
 * `designs/08-audits-and-process/2026-07-27-prod-log-audit-and-two-defects.md` finding C.
 */
export function stripSeedCursorsForForeignOrdering(
    seed: OpeningThoughtWithGroupMatchesDTO[] | undefined,
    requestedOrdering: DiscussionOpeningThoughtOrderingStyle,
    /**
     * The ordering the SEED describes, as the server declared it on the payload element
     * (`discussion.jte`'s `data-thoughts-ordering`). Defaults to the mirrored constant, which is what
     * a page rendered by an older template carries — the deploy halves stay order-independent and the
     * fallback is the value that was true before the attribute was read (register F20).
     */
    seedOrdering: DiscussionOpeningThoughtOrderingStyle = SSR_SEEDED_THOUGHTS_ORDERING,
): OpeningThoughtWithGroupMatchesDTO[] | undefined {
    if (seed === undefined || requestedOrdering === seedOrdering) {
        return seed;
    }
    return seed.map((item) => ({ ...item, paginationParameter: undefined }));
}

export type DiscussionOpeningThoughtOrderingStyle =
    | "best"
    | "controversial"
    | "new"
    | "old"
    | "agreed"
    | "disagreed"
    | "conversations";

export function normalizeDiscussionOpeningThoughtOrdering(value?: string | null): DiscussionOpeningThoughtOrderingStyle {
    switch ((value ?? "").trim().toLowerCase()) {
        case "best":
            return "best";
        case "controversial":
            return "controversial";
        case "old":
            return "old";
        case "new":
            return "new";
        case "agreed":
        case "top":
            return "agreed";
        case "disagreed":
            return "disagreed";
        case "conversations":
            return "conversations";
        default:
            return "best";
    }
}

/**
 * Is this string a value `normalizeDiscussionOpeningThoughtOrdering` actually RECOGNISES?
 *
 * <p>The normalizer answers `"best"` for anything it does not know, which is right for a URL parameter
 * a member can type and wrong for a server DECLARATION: a caller that has to distinguish "the server
 * said best" from "the server said something this build has never heard of" cannot, and would treat
 * the second as the first. `DiscussionPage`'s reader of `data-thoughts-ordering` is exactly that
 * caller — believing an unrecognised token as `best` would strip the cursors off a seed that is in the
 * default ordering, turning "load more" off for no reason.
 *
 * <p>The alias `top` is recognised on purpose: it is a spelling of `agreed`, not an unknown value.
 */
export function isRecognizedDiscussionOpeningThoughtOrdering(value?: string | null): boolean {
    const token = (value ?? "").trim().toLowerCase();
    if (token === "") {
        return false;
    }
    // Any token that is NOT recognised normalizes to "best". So a token is recognised when it either
    // normalizes to something other than "best", or IS one of the spellings of "best" itself.
    return normalizeDiscussionOpeningThoughtOrdering(token) !== "best" || token === "best";
}

export function normalizeDiscussionGroupPublicIds(
    value?: Array<string | null | undefined>,
): string[] {
    return Array.from(new Set((value ?? [])
        .map((entry) => entry?.trim() || "")
        .filter(Boolean)))
        .sort((left, right) => left.localeCompare(right));
}

export function buildDiscussionGroupPublicIdsCsv(
    value?: Array<string | null | undefined>,
): string | undefined {
    const normalized = normalizeDiscussionGroupPublicIds(value);
    return normalized.length > 0 ? normalized.join(",") : undefined;
}

export function buildDiscussionOpeningArgumentRequest(
    selectedGroupPublicIds?: Array<string | null | undefined>,
    orderingStyle?: string | null,
): {
    groupPublicIds?: string;
    openingArgumentOrdering: DiscussionOpeningThoughtOrderingStyle;
} {
    const groupPublicIds = buildDiscussionGroupPublicIdsCsv(selectedGroupPublicIds);
    const openingArgumentOrdering = normalizeDiscussionOpeningThoughtOrdering(orderingStyle);

    return {
        groupPublicIds,
        openingArgumentOrdering,
    };
}

export function areNormalizedStringArraysEqual(
    left?: Array<string | null | undefined>,
    right?: Array<string | null | undefined>,
): boolean {
    const normalizedLeft = normalizeDiscussionGroupPublicIds(left);
    const normalizedRight = normalizeDiscussionGroupPublicIds(right);

    if (normalizedLeft.length !== normalizedRight.length) {
        return false;
    }

    return normalizedLeft.every((value, index) => value === normalizedRight[index]);
}
